import { Suspense, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import Layout from '@/components/layout/Layout';
import { isChromeFreePrivatePath } from '@/components/route/isChromeFreePrivatePath';
import { PageContentFallback } from '@/components/route/PageFallback';
import { useRouteServerErrorRegistration } from '@/config/routeServerError';
import { IS_ADMIN_KEY } from '@/constants/roles';
import { LOGIN, MAGIC_LINK, ONBOARD, TERMS } from '@/constants/routes';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import useGtmDataLayerPush from '@/hooks/useGtmDataLayerPush';
import useLocale from '@/hooks/useLocale';
import { useUrlState } from '@/hooks/useUrlState';
import Error from '@/pages/Error';
import { useAppDispatch } from '@/store/hooks';
import { restoreSessionIfNeeded } from '@/store/sessionBootstrap';
import { logoutUser } from '@/store/userSlice';
import Loader from '@/UI/Loader';
import { isClientDirectLoginBlocked } from '@/utils/clientDirectLogin';
import {
  getAccessToken,
  getLocalStorageItem,
  removeLocalStorageItem,
  storePathname,
  storeSearch,
} from '@/utils/localStorage';

const PrivateRoute = () => {
  const [error, setError] = useState(false);
  const dispatch = useAppDispatch();
  const { history } = useUrlState();
  const location = useLocation();

  const token = getAccessToken();
  const isAdmin = getLocalStorageItem(IS_ADMIN_KEY);

  const {
    isAuth,
    loading,
    user,
    isAdmin: isAdminUser,
    isRecruiter,
    isGuest,
  } = useCurrentUser();
  const { browserLocale, setBrowserLocale } = useLocale();

  useRouteServerErrorRegistration(setError);

  useEffect(() => {
    if (isAdmin) {
      return;
    }

    if (user) {
      const userLanguage =
        user.default_language || user.company?.default_language;
      if (userLanguage && userLanguage !== browserLocale) {
        setBrowserLocale(userLanguage);
      }
    }
  }, [user, setBrowserLocale, browserLocale, isAdmin]);
  useGtmDataLayerPush(user);

  useEffect(() => {
    if (history.location.search.includes('magic_link')) {
      storePathname(history.location.pathname);
      storeSearch(history.location.search);
      history.push(MAGIC_LINK);
    } else {
      restoreSessionIfNeeded(dispatch);
      if (!token && !isAuth) {
        history.push(LOGIN);
      }
      if (user && isGuest) {
        void dispatch(logoutUser());
      }
      // Allow admin "login as" impersonation of clients; block real client
      // sessions unless the account is allowlisted for direct platform login.
      if (user && !isAdmin && isClientDirectLoginBlocked(user)) {
        // Clear the token synchronously so the session-restore effect cannot
        // re-authenticate this user before the async logoutUser resolves.
        removeLocalStorageItem('access_token');
        void dispatch(logoutUser());
        history.push(LOGIN);
        return;
      }
      if (user && !isAdminUser) {
        if (!isAdmin && user.onboarding_status !== 'completed') {
          history.push(ONBOARD);
          return;
        }
        if (
          !isAdmin &&
          isRecruiter &&
          (user.unagreed_agreement_ids?.length ?? 0) > 0
        ) {
          history.push(TERMS);
          return;
        }
      }
    }
  }, [
    dispatch,
    history,
    isAdmin,
    isAdminUser,
    isAuth,
    isGuest,
    isRecruiter,
    location.pathname,
    location.search,
    token,
    user,
  ]);

  if (error) {
    return <Error />;
  }
  if (loading) {
    return <Loader centered size="large" minHeight="100vh" />;
  }
  if (!isAuth) {
    return <Navigate to={LOGIN} replace />;
  }
  if (isChromeFreePrivatePath(location.pathname)) {
    return <Outlet />;
  }
  return (
    <Layout>
      <Suspense fallback={<PageContentFallback />}>
        <Outlet />
      </Suspense>
    </Layout>
  );
};

export default PrivateRoute;
